import {
  Box,
  DialogProps,
  Fade,
  IconButtonProps,
  IconProps,
  Modal,
  ModalProps,
  useTheme,
} from "@mui/material"
import { deepmerge } from "@mui/utils"
import { CloseIconButton } from "components/Button"
import { CloseIcon } from "components/icons"

import { useMemo } from "react"
import { getIcon } from "utils/icon"

export interface IBaseModalProps
  extends ModalProps,
    Pick<DialogProps, "TransitionComponent" | "TransitionProps"> {
  closeIconButtonProps?: IconButtonProps
  closeIconProps?: IconProps
  onClose: () => void
}

export const BaseModal: React.FC<IBaseModalProps> = ({
  open = false,
  onClose,
  sx = {},
  children,
  closeIconButtonProps = {},
  closeIconProps = {},
  TransitionComponent = Fade,
  TransitionProps = {},
  ...props
}) => {
  const theme = useTheme()
  const styleOverrides = theme.components[`BaseModal`]?.styleOverrides || {}
  const {
    root,
    container,
    closeIconButton,
    closeIcon: closeIconStyle,
  } = styleOverrides

  const closeIcon = useMemo(() => {
    const iconSx = deepmerge(closeIconStyle, closeIconProps?.sx || {})
    return (
      getIcon(theme?.assets?.closeIcon, {
        ...closeIconProps,
        sx: iconSx,
      }) || <CloseIcon {...closeIconProps} sx={iconSx} />
    )
  }, [theme, closeIconStyle, closeIconProps])

  return (
    <Modal
      disableAutoFocus
      sx={deepmerge(root, sx)}
      open={open}
      onClose={onClose}
      {...props}
    >
      <TransitionComponent in={open} {...TransitionProps}>
        <Box sx={container}>
          {children}
          <CloseIconButton
            {...closeIconButtonProps}
            icon={closeIcon}
            className="BaseModal-closeButton"
            sx={deepmerge(closeIconButton, closeIconButtonProps.sx || {})}
            onClick={onClose}
          />
        </Box>
      </TransitionComponent>
    </Modal>
  )
}
